import './index.scss'
import React, { useState, useEffect, useMemo } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Table, Button, Dropdown, message, Grid, Checkbox, Radio, Skeleton, Tooltip } from 'antd-v4'
import { saveAs } from 'file-saver'
import { InventoryOnHandGroupBy } from '@getgreenline/inventory/build/models/inventory'
import Helmet from 'react-helmet'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { ReportFilter } from '../../ReportFilter'
import { ReportFilterStore } from '../../ReportFilterStore'
import { COLORS } from '../../../../../../constants/Colors'
import { ColumnType } from 'antd-v4/lib/table/interface'
import { DownOutlined } from '@ant-design/icons'
import { columnsWithNumbers, getSorter, removeDollarSign } from './utils'
import Papa from 'papaparse'
import { ColumnKeys } from './enums'
import { LocalStorage } from '../../../../../../utilities/LocalStorage'
import { IncludeOutOfStockMenu } from './IncludeOutOfStockMenu'
import { ShowTableColumnsMenu } from './ShowColumnsMenu'
import { TotalsSummaryRow } from './Summary'
import { columnTitleMap, columnTitleTooltipMap } from './maps'
import { IInventoryOnHand, TInventoryOnHandColumns, TInventoryOnHandOptionalColumns } from './types'
import { fetchInventoryOnHand } from './api'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { pick } from 'lodash'

const { useBreakpoint } = Grid

export const InventoryOnHandReport: React.FC<{
  companyId: number
  reportFilterStore: ReportFilterStore
  currentCompanyStore: CurrentCompanyStore
}> = inject(
  'currentCompanyStore',
  'reportFilterStore',
)(
  observer(({ companyId, reportFilterStore, currentCompanyStore }) => {
    const { query } = reportFilterStore
    const isValidGroupBy =
      query.groupBy === InventoryOnHandGroupBy.PRODUCTS ||
      query.groupBy === InventoryOnHandGroupBy.CATEGORIES
    const [includeOutOfStock, setIncludeOutOfStock] = useState(query.includeOutOfStock === 'true')
    const [includeSubCategories, setIncludeSubCategories] = useState(
      query.includeSubCategories === 'true',
    )
    const [groupBy, setGroupBy] = useState<InventoryOnHandGroupBy>(
      isValidGroupBy ? (query.groupBy as InventoryOnHandGroupBy) : InventoryOnHandGroupBy.PRODUCTS,
    )
    const [inventoryOnHand, setInventoryOnHand] =
      useState<IInventoryOnHand[] | undefined>(undefined)
    const [offset] = useState(Number(query.offset) || 0)
    const [limit] = useState(Number(query.limit) || 10000)
    const [shownColumns, setShownColumns] = useState<TInventoryOnHandColumns>(
      LocalStorage.getInventoryOnHandTableColumns() || [ColumnKeys.CATEGORY, ColumnKeys.UNIT_PRICE],
    )
    const [showTableColumnsMenuVisible, setShowTableColumnsMenuVisible] = useState(false)
    const updateShownColumns = (columns: TInventoryOnHandColumns) => {
      setShownColumns(columns)
      LocalStorage.setInventoryOnHandTableColumns(columns)
    }
    const columnDataKeys = inventoryOnHand?.length ? Object.typedKeys(inventoryOnHand[0]) : []
    const optionalColumns: TInventoryOnHandOptionalColumns = {
      [ColumnKeys.KEY]: { isShown: false },
      [ColumnKeys.SUB_CATEGORY]: { isShown: query.includeSubCategories === 'true' },
      [ColumnKeys.MARGIN]: {
        isShown:
          shownColumns.includes(ColumnKeys.MARGIN) ||
          query.groupBy === InventoryOnHandGroupBy.CATEGORIES,
      },
      [ColumnKeys.BRAND]: { isShown: shownColumns.includes(ColumnKeys.BRAND) },
      [ColumnKeys.CATEGORY]: {
        isShown:
          shownColumns.includes(ColumnKeys.CATEGORY) ||
          query.groupBy === InventoryOnHandGroupBy.CATEGORIES,
      },
      [ColumnKeys.UNIT_PRICE]: { isShown: shownColumns.includes(ColumnKeys.UNIT_PRICE) },
    }
    const isLoading = !inventoryOnHand
    const unloadInventoryOnHand = () => {
      setInventoryOnHand(undefined)
    }
    const { lg } = useBreakpoint()
    const filter = {
      groupBy,
      includeOutOfStock,
      includeSubCategories,
      locationIds: [...(reportFilterStore.selectedLocationIds || [])],
      startDate: reportFilterStore.startDate,
      offset,
      limit,
    }
    const hasFilterChanged = reportFilterStore.getFiltersChanged(filter).length > 0

    const filteredColumnDataKeys = [
      'key',
      ...columnDataKeys.filter(
        (column) => !optionalColumns[column] || optionalColumns[column]?.isShown,
      ),
    ]

    const filterHiddenColumns = (inventoryOnHand: IInventoryOnHand[]) =>
      inventoryOnHand.map((item) => pick(item, filteredColumnDataKeys))

    const exportToCSV = async () => {
      const date = reportFilterStore.startDate

      if (!inventoryOnHand) return

      const inventoryOnHandWithoutDollarSign = removeDollarSign(inventoryOnHand)

      const inventoryOnHandWithoutHiddenColumns = filterHiddenColumns(
        inventoryOnHandWithoutDollarSign,
      )

      let inventoryOnHandCSV = Papa.unparse(inventoryOnHandWithoutHiddenColumns)

      columnDataKeys.forEach((key) => {
        inventoryOnHandCSV = inventoryOnHandCSV.replace(key, columnTitleMap[key])
      })

      saveAs(
        new Blob([inventoryOnHandCSV || '']),
        `${groupBy}-on-hand-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`,
      )
    }

    const columns = columnDataKeys.reduce((columns: ColumnType<IInventoryOnHand>[], dataKey) => {
      const isHiddenColumn = optionalColumns[dataKey] && !optionalColumns[dataKey]?.isShown
      if (isHiddenColumn) return columns
      const isNumericColumn = columnsWithNumbers.includes(dataKey)
      const title = columnTitleMap[dataKey]
      const tooltip = columnTitleTooltipMap[title]
      columns.push({
        title: (
          <Tooltip placement='top' title={tooltip}>
            <span>{title}</span>
          </Tooltip>
        ),
        dataIndex: dataKey,
        width: 93,
        align: isNumericColumn ? 'right' : 'left',
        className: isNumericColumn ? 'cell-pr-2 nowrap' : '',
        sortDirections: ['descend', 'ascend', 'descend'],
        sorter: getSorter(dataKey),
        showSorterTooltip: false,
      })
      return columns
    }, [])

    const Summary = useMemo(
      () => inventoryOnHand && TotalsSummaryRow(inventoryOnHand, columnDataKeys, optionalColumns),
      [isLoading, optionalColumns],
    )

    useEffect(() => {
      if (!isLoading) return
      reportFilterStore.saveToQuery(filter)
      if (!reportFilterStore.selectedLocationIds.length) return setInventoryOnHand([])
      const { selectedLocationIds: locationIds, startDate } = reportFilterStore
      fetchInventoryOnHand({
        groupBy,
        companyId,
        locationIds,
        startDate,
        includeOutOfStock,
        includeSubCategories,
      })
        .then((res) => setInventoryOnHand(res || []))
        .catch((error) => {
          setInventoryOnHand([])
          console.error(error)
          message.error(
            parseErrorMsg(error) || 'Something went wrong, please refresh and try again',
          )
        })
    }, [isLoading])

    return (
      <>
        <Helmet>
          <title>Reports | Inventory On Hand</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter
          maxRangeMonths={12}
          hideDates
          showSingleDate
          apply={unloadInventoryOnHand}
          buttonType={hasFilterChanged ? 'primary' : 'ghost'}
        />
        <section className={`${lg ? 'rounded' : ''} background-white p-3`}>
          <header>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 'bolder', color: COLORS.grey600 }}>
              Inventory on-hand
            </h2>
          </header>

          <aside
            style={{
              margin: '0.5rem 0 1rem 0',
              display: 'grid',
              gap: 8,
              justifyContent: 'start',
              alignItems: 'center',
              gridAutoFlow: lg ? 'column' : 'row',
            }}
          >
            <Radio.Group
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              style={{ minWidth: 185 }}
            >
              <Radio.Button value={InventoryOnHandGroupBy.PRODUCTS}>Products</Radio.Button>
              <Radio.Button value={InventoryOnHandGroupBy.CATEGORIES}>Categories</Radio.Button>
            </Radio.Group>
            <Dropdown
              overlay={() => IncludeOutOfStockMenu(setIncludeOutOfStock, includeOutOfStock)}
              trigger={['click']}
            >
              <Button>
                Show {includeOutOfStock ? 'all' : 'in-stock only'} <DownOutlined />
              </Button>
            </Dropdown>
            <Checkbox
              disabled={groupBy !== InventoryOnHandGroupBy.CATEGORIES}
              defaultChecked={includeSubCategories}
              onChange={() => setIncludeSubCategories(!includeSubCategories)}
              className='text-center'
            >
              Include sub categories
            </Checkbox>
            <Dropdown
              overlay={() =>
                ShowTableColumnsMenu({
                  onChange: updateShownColumns,
                  defaultColumns: shownColumns,
                })
              }
              trigger={['click']}
              visible={showTableColumnsMenuVisible}
              onVisibleChange={(isVisible) => setShowTableColumnsMenuVisible(isVisible)}
              disabled={groupBy !== InventoryOnHandGroupBy.PRODUCTS}
            >
              <Button>
                Table columns <DownOutlined />
              </Button>
            </Dropdown>
            <Button type='ghost' onClick={exportToCSV}>
              Export to CSV
            </Button>
          </aside>
          <main>
            {isLoading && <Skeleton active={true} />}
            <Table
              style={{ opacity: isLoading ? 0 : 1, height: isLoading ? 0 : '100%' }}
              dataSource={inventoryOnHand}
              columns={columns}
              scroll={{ y: '60vh' }}
              pagination={false}
              className='show-horizontal-scrollbar'
              summary={() => Summary}
            ></Table>
          </main>
        </section>
      </>
    )
  }),
)
